import { DateTime, IANAZone } from 'luxon';
import { luxonLocalizer } from 'react-big-calendar';

export const assertCalendarZone = (zone) => {
  if (typeof zone !== 'string' || !IANAZone.isValidZone(zone)) {
    throw new Error('calendar_timezone_unavailable');
  }
  return zone;
};

export const clinicDateTime = (value, zone) => DateTime.fromJSDate(value, { zone: assertCalendarZone(zone) });

export const createClinicLocalizer = (zone) => {
  assertCalendarZone(zone);
  // RBC's Luxon adapter uses these four factories. Bind their zone locally;
  // changing Luxon Settings.defaultZone would affect the rest of the app.
  const localizer = luxonLocalizer({
    fromJSDate: (value) => DateTime.fromJSDate(value, { zone }),
    local: () => DateTime.now().setZone(zone),
    min: (...values) => DateTime.min(...values),
    max: (...values) => DateTime.max(...values),
  });
  // RBC's gutter uses a 24-hour wall-clock grid. Compensate elapsed minutes
  // with this clinic's offset change, never the native browser's DST change.
  localizer.getDstOffset = (start, end) => clinicDateTime(end, zone).offset - clinicDateTime(start, zone).offset;
  localizer.getTotalMin = (start, end) => localizer.diff(start, end, 'minutes') + localizer.getDstOffset(start, end);
  localizer.getMinutesFromMidnight = (date) => {
    const start = localizer.startOf(date, 'day');
    return localizer.diff(start, date, 'minutes') + localizer.getDstOffset(start, date);
  };
  localizer.getTimezoneOffset = (date) => -clinicDateTime(date, zone).offset;
  localizer.minutes = (date) => clinicDateTime(date, zone).minute;
  localizer.startAndEndAreDateOnly = (start, end) => [start, end].every(date => {
    const value = clinicDateTime(date, zone);
    return +value === +value.startOf('day');
  });
  return localizer;
};

export const formatDateForApi = (date, zone) => clinicDateTime(date, zone).toISODate();

const allowedRange = (zone, limits, now = new Date()) => {
  const today = clinicDateTime(now, zone).startOf('day');
  const days = ['free', 'basic'].includes(limits.tier) ? 1 : limits.calendar_view_days;
  return days === 4
    ? { start: today.minus({ days: 1 }), end: today.plus({ days: 2 }).endOf('day') }
    : { start: today, end: today.plus({ days: Math.max(1, days) - 1 }).endOf('day') };
};

export const isClinicDateAllowed = (date, zone, limits, now = new Date()) => {
  if (!limits.is_limited) return true;
  const { start, end } = allowedRange(zone, limits, now);
  const value = clinicDateTime(date, zone);
  return value >= start && value <= end;
};

export const calendarRange = (date, view, zone, limits, now = new Date()) => {
  const current = clinicDateTime(date, zone);
  let start;
  let end;
  if (view === 'day') {
    start = current.startOf('day'); end = current.endOf('day');
  } else if (view === 'week') {
    start = current.minus({ days: current.weekday % 7 }).startOf('day');
    end = start.plus({ days: 6 }).endOf('day');
  } else if (view === 'agenda') {
    start = current.startOf('day'); end = start.plus({ days: 14 }).endOf('day');
  } else if (view === 'year') {
    start = current.startOf('year'); end = current.endOf('year');
  } else if (view === '6months') {
    start = current.startOf('month'); end = start.plus({ months: 6 }).minus({ milliseconds: 1 });
  } else {
    start = current.startOf('month'); end = current.endOf('month');
  }
  const originalStart = start;
  const originalEnd = end;
  if (limits.is_limited) {
    const allowed = allowedRange(zone, limits, now);
    start = DateTime.max(start, allowed.start);
    end = DateTime.min(end, allowed.end);
    if (end < start) { start = allowed.start; end = allowed.end; }
  }
  return {
    startDate: start.toJSDate(), endDate: end.toJSDate(),
    tierInfo: { wasLimited: +start !== +originalStart || +end !== +originalEnd, tier: limits.tier },
  };
};

export const navigateCalendar = (date, view, amount, zone) => clinicDateTime(date, zone)
  .plus(view === 'week' ? { weeks: amount } : ['month', 'agenda'].includes(view) ? { months: amount }
    : view === 'year' ? { years: amount } : view === '6months' ? { months: amount * 6 } : { days: amount }).toJSDate();

export const displayCalendarDate = (date, view, zone) => {
  const current = clinicDateTime(date, zone).setLocale('en-GB');
  if (view === 'day') return current.toFormat('cccc, d LLLL yyyy');
  if (view === 'week') {
    const start = current.minus({ days: current.weekday % 7 });
    return `${start.toFormat('d LLL')} to ${start.plus({ days: 6 }).toFormat('d LLL yyyy')}`;
  }
  return current.toFormat('LLLL yyyy');
};

// The legacy detail reader returns UTC database timestamps without an offset.
// This interpretation is confined to the KSL calendar's read-only detail view.
export const parseCalendarInstant = value => {
  if (value == null) return null;
  const parsed = DateTime.fromISO(value, { zone: 'UTC', setZone: true });
  if (!parsed.isValid) throw new Error('calendar_timestamp_unavailable');
  return parsed.toJSDate();
};
